import { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Actions from '../../../../actions'
import Operations from '../../../../operations'
import Selectors from '../../../../selectors'
import { find } from 'lodash'
import RiskAssessmentItem from './components/RiskAssessmentItem'
import RiskAssessmentSortType from './components/RiskAssessmentSortType'
import { RiskAssessmentPaywall } from './components/RiskAssessmentPaywall'
import { HelpLink, Spinner, SearchSelect } from '../../../../components/generic'
import { colors, sizes } from '@lmc/common/constants'
import { getNominative } from '../../../../common/utils'
import { withRouter } from '../../../../withRouter'
import { history } from '../../../../store'

import {
  ButtonOLD,
  EmptyState,
  NoDataIcon,
  NotStartedIcon,
  PageHeader,
} from '@lmc/ui-kit'
import { isBrowser, isTablet } from 'react-device-detect'
import { ProTag } from 'components/ProTag'

const isDesktop = isBrowser || isTablet

export class ResidentRiskAssessmentDashboardScreen extends Component {
  state = {
    loading: null,
  }

  componentDidMount() {
    const {
      router: { params },
      setSortType,
    } = this.props
    this.loadData(params)
    setSortType('high-low')
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.router.params !== nextProps.router.params) {
      this.setState({ loading: true })
      this.loadData(nextProps.router.params)
    }
    if (this.props.riskAssessments !== nextProps.riskAssessments) {
      this.setState({ loading: false })
    }
  }

  loadData = (params) => {
    const query = { resident: params.id }
    if (params.risk_assessment_id) {
      query.riskAssessment = params.risk_assessment_id
    } else {
      query.mostRecent = true
    }
    this.props.loadList('risk-assessments', query)
  }

  filterRiskAssessments = (current) =>
    this.props.riskAssessments.filter((assessment) =>
      current ? assessment.hasCarePlans : !assessment.hasCarePlans
    )

  getTitle = () => {
    const {
      router: { params },
      resident,
      riskAssessments,
    } = this.props

    if (params.risk_assessment_id) {
      const assessment = find(riskAssessments, {
        id: params.risk_assessment_id,
      })
      return assessment ? `History - ${assessment.form.name}` : 'History'
    }
    const name = `${resident.name.first} ${resident.name.last}`
    return `${name}'s Risk Assessments`
  }

  handleRiskAssessmentClick = (id) => {
    const {
      router: { params },
      hasAccess,
    } = this.props

    const hasRolePermission = hasAccess(
      ['carehome-admin', 'senior-carer'],
      [
        {
          i: 'view',
          this: 'RiskAssessment',
        },
      ]
    )

    if (!hasRolePermission) {
      return
    }

    let url = `/residents/${params.id}/risk-assessments/${id}`
    if (params.risk_assessment_id) {
      url = `/residents/${params.id}/risk-assessments/${params.risk_assessment_id}/history/${id}`
    }

    history.push(url)
  }

  renderEmptyHistoricalSection = () => {
    const {
      router: { params },
      resident,
      carePlanNominative,
    } = this.props
    const HELP_MESSAGE = params.risk_assessment_id
      ? NO_HISTORICAL_ASSESSMENTS
      : getNoLinkedAssessments(`${resident.name.first}`, carePlanNominative)

    const SUPPORT_ARTICLE = params.risk_assessment_id
      ? NO_HISTORICAL_ASSESSMENTS_ARTICLE
      : NO_UNLINKED_ASSESSMENTS_ARTICLE

    return (
      <EmptyState
        title="Nothing to see here!"
        description={HELP_MESSAGE}
        icon={<NoDataIcon />}
      >
        <a
          href={SUPPORT_ARTICLE}
          rel="noreferrer"
          style={styles.emptyMessageLink}
          target="_blank"
        >
          Find out more
        </a>
      </EmptyState>
    )
  }

  renderInitialAssessmentInfo = (resident) => (
    <EmptyState
      title={`Finalise ${resident.name.first}'s Initial Assessment to start adding Risk Assessments`}
      icon={<NotStartedIcon />}
    >
      {getInitialAssessmentInfo(
        this.props.carePlanNominative,
        this.props.preferredPeopleNominative
      )}
      <a href={PRE_ASSESSMENT_ARTICLE} rel="noreferrer" target="_blank">
        Find out more
      </a>
    </EmptyState>
  )

  renderEmptyPage = () => (
    <EmptyState
      icon={<NoDataIcon />}
      title="Shoot... No data to show you"
      description="Press that blue button at the top right of the page."
    >
      <a href={EMPTY_DASHBOARD_ARTICLE} rel="noreferrer" target="_blank">
        Find out more
      </a>
    </EmptyState>
  )

  renderSection = ({ current }) => {
    const {
      carers,
      sortType,
      carePlanNominative,
      router: { params },
    } = this.props

    const title = current
      ? 'Current'
      : `${
          params?.risk_assessment_id
            ? 'History'
            : `Not attached to any ${getNominative(
                carePlanNominative,
                'Care Plan'
              )}`
        }`
    const filteredAssessments = this.filterRiskAssessments(current)
    const Section = filteredAssessments.length
      ? filteredAssessments.map((assessment) => {
          const carer = find(carers, { id: assessment.createdBy })
          return (
            <RiskAssessmentItem
              carer={carer}
              key={assessment.id}
              riskAssessment={assessment}
              onView={this.handleRiskAssessmentClick}
              carePlanNominative={carePlanNominative}
            />
          )
        })
      : this.renderEmptyHistoricalSection()

    return (
      <div style={styles.sectionContainer}>
        <div style={styles.sectionTitle}>
          {title}
          {this.renderSortType()}
        </div>
        {this.state.loading || !sortType ? <Spinner /> : Section}
      </div>
    )
  }

  renderSortType = () => {
    const {
      router: { params },
      setSortType,
      sortType,
    } = this.props
    return (
      <div
        style={{
          ...styles.sortContainer,
          paddingTop: params.risk_assessment_id ? 10 : 30,
        }}
      >
        <span style={styles.sortBy}>Sort by:</span>
        <RiskAssessmentSortType
          params={params}
          onChange={setSortType}
          sortType={sortType}
        />
      </div>
    )
  }

  renderDropdown = () => {
    const {
      router: { params },
      riskAssessments,
    } = this.props
    if (params.risk_assessment_id) return null

    return (
      <SearchSelect
        getOptionLabel={(option) => option.form.name}
        getOptionValue={(option) => option.riskScore || option.form.name}
        options={riskAssessments}
        placeholder={DROPDOWN_PLACEHOLDER}
        onChange={(option) => this.handleRiskAssessmentClick(option.id)}
      />
    )
  }

  render() {
    const {
      carers,
      resident,
      riskAssessments,
      hasCarePlans,
      router,
      hasAccess,
    } = this.props

    const { params } = router

    if (!hasCarePlans) {
      return <RiskAssessmentPaywall />
    }

    if (!carers || !resident || !riskAssessments) {
      return <Spinner />
    }

    const isInitialAssessment = resident.status === 'pre-assessment'

    const isHistoryRiskAssessmentsRoute =
      router?.location?.pathname?.includes('history')

    const hasRolePermission = hasAccess(
      ['carehome-admin', 'senior-carer'],
      [
        {
          i: 'create',
          this: 'RiskAssessment',
        },
      ]
    )

    return (
      <div style={styles.container}>
        <PageHeader
          id="risk-assessment-dashboard-title"
          title={this.getTitle()}
          sticky
          onBack={
            !isDesktop
              ? () => history.replace?.(`/residents/${params.id}/profile`)
              : undefined
          }
        >
          <ProTag />
          {!isHistoryRiskAssessmentsRoute && (
            <ButtonOLD
              type="primary"
              onClick={() => this.props.openRiskAssessmentModal(undefined)}
              disabledByPermission={!hasRolePermission}
            >
              New risk assessment
            </ButtonOLD>
          )}
        </PageHeader>
        {riskAssessments.length ? (
          <div>
            {this.renderDropdown()}
            {this.renderSection({ current: true })}
            {this.renderSection({ current: false })}
          </div>
        ) : isInitialAssessment ? (
          this.renderInitialAssessmentInfo(resident)
        ) : (
          this.renderEmptyPage()
        )}
        <HelpLink
          supportLink={EMPTY_DASHBOARD_ARTICLE}
          style={styles.supportLink}
        />
      </div>
    )
  }
}

const DROPDOWN_PLACEHOLDER = 'Search for current and unattached assessments'
const NO_HISTORICAL_ASSESSMENTS =
  'Older versions of this risk assessment will appear here after you have performed reviews.'
const EMPTY_DASHBOARD_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3371486-risk-assessment-dashboard'
const NO_HISTORICAL_ASSESSMENTS_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3353673-how-can-i-view-older-versions-of-a-risk-assessment'
const NO_UNLINKED_ASSESSMENTS_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3371462-how-do-i-remove-a-risk-assessment-from-a-care-plan'
const PRE_ASSESSMENT_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3376601-how-do-i-finalise-a-initial-assessment'
const getInitialAssessmentInfo = (
  carePlanNominative,
  preferredPeopleNominative
) =>
  `When you finalise the Initial Assessment for this ${getNominative(
    preferredPeopleNominative,
    'resident'
  )}, you’ll be able to add Risk Assessments to their ${getNominative(
    carePlanNominative,
    'Care Plan'
  )}.`
const getNoLinkedAssessments = (name, carePlanNominative) =>
  `Risk assessments will appear here if they are no longer attached to any of ${name}'s ${getNominative(
    carePlanNominative,
    'Care Plans'
  )}.`

ResidentRiskAssessmentDashboardScreen.propTypes = {
  carers: PropTypes.array,
  loadList: PropTypes.func.isRequired,
  resident: PropTypes.object,
  riskAssessments: PropTypes.array,
  hasCarePlans: PropTypes.bool,
}

const styles = {
  container: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    padding: '0 0 50px 0',
  },
  headerContainer: {
    marginBottom: 14,
  },
  divider: {
    height: 2,
    marginBottom: 18,
    marginTop: 8,
    width: '100%',
  },
  emptyMessage: {
    marginTop: 5,
  },
  emptyMessageLink: {
    marginLeft: 5,
  },
  noAssessments: {
    marginTop: 24,
    marginBottom: 20,
  },
  sectionContainer: {
    paddingTop: 10,
    paddingBottom: 30,
  },
  sectionTitle: {
    borderBottom: '1px solid',
    borderColor: colors.grey,
    fontSize: 16,
    padding: 8,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  sortBy: {
    color: colors.darkgrey,
    display: 'block',
  },
  sortContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  supportLink: {
    display: 'inline-block',
    float: 'left',
    marginTop: 0,
  },
  title: {
    marginBottom: 0,
  },
  topContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: 30,
  },
}

const RISK_ASSESSMENT_MODAL_ID = 'createRiskAssessment'

const mapStateToProps = (state, props) => ({
  carers: state.data.carers,
  resident: find(state.data.residents, { id: props.router.params.id }),
  riskAssessments: Selectors.sortRiskAssessments(state),
  sortType: state.carePlans.riskAssessmentSortType,
  carePlanNominative: state.global.home.preferredCarePlansNominative,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  hasCarePlans: state.global.home.features.carePlans.enabled,
  hasAccess: (roles, abilities) =>
    Selectors.checkRoleAccess(state, { roles, abilities }),
})

const mapDispatchToProps = (dispatch) => ({
  loadList: (list, query) => dispatch(Operations.loadList(list, query)),
  setSortType: (type) => dispatch(Actions.setRiskAssessmentSortType(type)),
  openRiskAssessmentModal: (completedRiskAssessment) => {
    dispatch(
      Actions.openModal({
        modalId: RISK_ASSESSMENT_MODAL_ID,
        data: { completedRiskAssessment },
      })
    )
  },
})

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(ResidentRiskAssessmentDashboardScreen)
)
